import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  Users, 
  Heart, 
  AlertTriangle, 
  CheckCircle2, 
  TrendingUp,
  Brain,
  Sparkles,
  Target,
  MessageCircle,
  Scale,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

export default function MatchCalculator() {
  const navigate = useNavigate();
  const [myProfile, setMyProfile] = useState<any>(null);
  const [step, setStep] = useState<'input' | 'results'>('input');
  const [missingSetup, setMissingSetup] = useState(false);
  
  // Partner's profile data
  const [partnerName, setPartnerName] = useState('');
  const [partnerAge, setPartnerAge] = useState('');
  const [partnerValues, setPartnerValues] = useState<string[]>([]);
  const [partnerGoals, setPartnerGoals] = useState<string[]>([]);
  const [partnerCommunicationStyle, setPartnerCommunicationStyle] = useState<'direct' | 'subtle' | 'balanced' | 'unsure'>('balanced');
  const [partnerConflictStyle, setPartnerConflictStyle] = useState<'address-immediately' | 'need-space' | 'avoid' | 'unsure'>('unsure');
  const [partnerSocialPreference, setPartnerSocialPreference] = useState(50);
  const [partnerActivityLevel, setPartnerActivityLevel] = useState(50);
  const [partnerAttachment, setPartnerAttachment] = useState<'secure' | 'anxious' | 'avoidant' | 'fearful' | 'unsure'>('unsure');
  
  // Compatibility results
  const [compatibilityScore, setCompatibilityScore] = useState(0);
  const [analysis, setAnalysis] = useState<any>(null);

  useEffect(() => {
    const stored = localStorage.getItem('tension_compatibility_profile');
    if (!stored) {
      setMissingSetup(true);
      return;
    }
    setMyProfile(JSON.parse(stored));
  }, [navigate]);

  useEffect(() => {
    if (missingSetup) {
    toast.error('Please complete your readiness profile first');
    }
  }, [missingSetup]);

  const valueOptions = [
    { key: 'honesty', label: 'Honesty' },
    { key: 'loyalty', label: 'Loyalty' },
    { key: 'growth', label: 'Personal Growth' },
    { key: 'family', label: 'Family' },
    { key: 'career', label: 'Career Ambition' },
    { key: 'adventure', label: 'Adventure' },
    { key: 'stability', label: 'Stability' },
    { key: 'creativity', label: 'Creativity' },
    { key: 'spirituality', label: 'Spirituality' },
    { key: 'health', label: 'Health/Fitness' },
    { key: 'community', label: 'Community' },
    { key: 'independence', label: 'Independence' },
  ];

  const goalOptions = [
    { key: 'marriage', label: 'Marriage' },
    { key: 'children', label: 'Children' },
    { key: 'no-children', label: 'No Children' },
    { key: 'career-growth', label: 'Career Growth' },
    { key: 'travel', label: 'Travel' },
    { key: 'home-ownership', label: 'Home Ownership' },
    { key: 'entrepreneurship', label: 'Entrepreneurship' },
    { key: 'relocation', label: 'Relocation' },
    { key: 'education', label: 'Further Education' },
    { key: 'financial-freedom', label: 'Financial Freedom' },
  ];

  const toggleValue = (value: string) => {
    setPartnerValues(prev => 
      prev.includes(value) 
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  };

  const toggleGoal = (goal: string) => {
    setPartnerGoals(prev => 
      prev.includes(goal) 
        ? prev.filter(g => g !== goal)
        : [...prev, goal]
    );
  };

  const calculateCompatibility = () => {
    if (!partnerName.trim()) {
      toast.error('Please enter their name');
      return;
    }

    if (partnerValues.length === 0) {
      toast.error('Please select at least one core value');
      return;
    }

    // Calculate compatibility score (0-100)
    let score = 0;
    let weights = 0;
    const insights = {
      strengths: [] as string[],
      challenges: [] as string[],
      recommendations: [] as string[],
      valueAlignment: 0,
      goalAlignment: 0,
      lifestyleAlignment: 0,
      communicationAlignment: 0,
      attachmentCompatibility: 0,
    };

    // 1. Values Alignment (30% weight)
    const valueOverlap = partnerValues.filter(v => myProfile.values.coreValues.includes(v)).length;
    const valueScore = (valueOverlap / Math.max(partnerValues.length, myProfile.values.coreValues.length)) * 100;
    insights.valueAlignment = Math.round(valueScore);
    score += valueScore * 0.3;
    weights += 0.3;

    if (valueScore >= 70) {
      insights.strengths.push(`Strong value alignment: ${valueOverlap} shared core values`);
    } else if (valueScore < 40) {
      insights.challenges.push('Limited overlap in core values may require extra communication');
    }

    // 2. Life Goals Alignment (25% weight)
    const goalOverlap = partnerGoals.filter(g => myProfile.values.lifeGoals.includes(g)).length;
    const goalScore = partnerGoals.length > 0 
      ? (goalOverlap / Math.max(partnerGoals.length, myProfile.values.lifeGoals.length)) * 100
      : 50;
    insights.goalAlignment = Math.round(goalScore);
    score += goalScore * 0.25;
    weights += 0.25;

    if (goalScore >= 70) {
      insights.strengths.push('Aligned life goals and future vision');
    } else if (goalScore < 40) {
      insights.challenges.push('Different life goals - discuss priorities early');
      insights.recommendations.push('Have an open conversation about your 5-year plans');
    }

    // 3. Lifestyle Compatibility (20% weight)
    const socialDiff = Math.abs(myProfile.lifestyle.socialPreference - partnerSocialPreference);
    const activityDiff = Math.abs(myProfile.lifestyle.activityLevel - partnerActivityLevel);
    const lifestyleScore = 100 - ((socialDiff + activityDiff) / 2);
    insights.lifestyleAlignment = Math.round(lifestyleScore);
    score += lifestyleScore * 0.2;
    weights += 0.2;

    if (lifestyleScore >= 70) {
      insights.strengths.push('Compatible lifestyle preferences and energy levels');
    } else if (lifestyleScore < 50) {
      insights.challenges.push('Different social and activity preferences');
      insights.recommendations.push('Find a balance between your different energy levels');
    }

    // 4. Communication Compatibility (15% weight)
    let commScore = 50;
    if (myProfile.communication.style === partnerCommunicationStyle) {
      commScore = 90;
      insights.strengths.push('Matching communication styles');
    } else if (
      (myProfile.communication.style === 'balanced' || partnerCommunicationStyle === 'balanced')
    ) {
      commScore = 70;
    } else {
      commScore = 40;
      insights.challenges.push('Different communication styles require awareness');
      insights.recommendations.push('Learn each other\'s communication preferences early');
    }
    insights.communicationAlignment = Math.round(commScore);
    score += commScore * 0.15;
    weights += 0.15;

    // 5. Attachment Compatibility (10% weight)
    const attachmentMatrix: Record<string, Record<string, number>> = {
      secure: { secure: 95, anxious: 75, avoidant: 70, fearful: 65, unsure: 60 },
      anxious: { secure: 75, anxious: 50, avoidant: 30, fearful: 45, unsure: 55 },
      avoidant: { secure: 70, anxious: 30, avoidant: 55, fearful: 40, unsure: 55 },
      fearful: { secure: 65, anxious: 45, avoidant: 40, fearful: 50, unsure: 50 },
      unsure: { secure: 60, anxious: 55, avoidant: 55, fearful: 50, unsure: 60 },
    };
    
    const attachScore = attachmentMatrix[myProfile.patterns.attachmentStyle]?.[partnerAttachment] || 50;
    insights.attachmentCompatibility = attachScore;
    score += attachScore * 0.1;
    weights += 0.1;

    if (attachScore >= 70) {
      insights.strengths.push('Compatible attachment styles');
    } else if (attachScore < 50) {
      insights.challenges.push('Attachment style differences may create tension');
      insights.recommendations.push('Consider reading about attachment theory together');
    }

    // Normalize score
    const finalScore = Math.round(score / weights);
    setCompatibilityScore(finalScore);
    setAnalysis(insights);
    setStep('results');

    // Save this calculation
    const calculations = JSON.parse(localStorage.getItem('tension_match_calculations') || '[]');
    calculations.push({
      partnerName,
      calculatedAt: new Date().toISOString(),
      score: finalScore,
      insights,
    });
    localStorage.setItem('tension_match_calculations', JSON.stringify(calculations));

    toast.success('Alignment score calculated!');
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score: number) => {
    if (score >= 70) return 'bg-green-100 border-green-200';
    if (score >= 50) return 'bg-yellow-100 border-yellow-200';
    return 'bg-red-100 border-red-200';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent Alignment';
    if (score >= 70) return 'Strong Alignment';
    if (score >= 60) return 'Good Potential';
    if (score >= 50) return 'Moderate Alignment';
    if (score >= 40) return 'Some Challenges';
    return 'Significant Differences';
  };

  if (!myProfile) {
    if (missingSetup) {
      return (
        <div className="min-h-screen tension-page-bg flex items-center justify-center px-6">
          <Card className="max-w-xl p-6 space-y-4 text-center">
            <h2 className="text-xl font-medium text-neutral-900">Alignment calculator needs your singles profile</h2>
            <p className="text-sm text-neutral-600">
              Complete onboarding to generate your readiness profile, then return to compare alignment.
            </p>
            <div className="flex justify-center gap-3">
              <Button onClick={() => navigate('/singles-onboarding')}>Go to onboarding</Button>
              <Button variant="outline" onClick={() => navigate('/compatibility-profile')}>Open profile</Button>
            </div>
          </Card>
        </div>
      );
    }
    return (
      <div className="min-h-screen tension-page-bg flex items-center justify-center">
        <p className="text-neutral-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen tension-page-bg">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            onClick={() => step === 'input' ? navigate(-1) : setStep('input')}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-600" />
          </button>
          <div>
            <h1 className="text-xl font-light">Relationship Alignment Calculator</h1>
            <p className="text-sm text-neutral-600">
              {step === 'input' ? 'Enter their details' : 'Alignment Analysis'}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {step === 'input' && (
          <div className="space-y-6">
            {/* Info Card */}
            <Card className="p-4 bg-blue-50 border-blue-200">
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-medium mb-1">How to use this calculator</p>
                  <p>Answer these questions based on what you know about them from conversations or observations. The more accurate, the better the alignment insight.</p>
                </div>
              </div>
            </Card>

            {/* Basic Info */}
            <Card className="p-6 space-y-4">
              <h2 className="font-medium flex items-center gap-2">
                <Users className="w-5 h-5 text-violet-600" />
                Basic Information
              </h2>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-2">
                    Their Name or Nickname
                  </label>
                  <Input
                    value={partnerName}
                    onChange={(e) => setPartnerName(e.target.value)}
                    placeholder="e.g., Alex"
                    className="max-w-xs"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-2">
                    Their Age (optional)
                  </label>
                  <Input
                    type="number"
                    value={partnerAge}
                    onChange={(e) => setPartnerAge(e.target.value)}
                    placeholder="e.g., 28"
                    className="max-w-xs"
                  />
                </div>
              </div>
            </Card>

            {/* Core Values */}
            <Card className="p-6 space-y-4">
              <h2 className="font-medium flex items-center gap-2">
                <Heart className="w-5 h-5 text-pink-600" />
                Their Core Values
              </h2>
              <p className="text-sm text-neutral-600">
                Select the values that seem most important to them (choose 3-5)
              </p>
              
              <div className="flex flex-wrap gap-2">
                {valueOptions.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => toggleValue(key)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      partnerValues.includes(key)
                        ? 'bg-violet-600 text-white'
                        : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Card>

            {/* Life Goals */}
            <Card className="p-6 space-y-4">
              <h2 className="font-medium flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-600" />
                Their Life Goals
              </h2>
              <p className="text-sm text-neutral-600">
                What are their major life goals? (select all that apply)
              </p>
              
              <div className="flex flex-wrap gap-2">
                {goalOptions.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => toggleGoal(key)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      partnerGoals.includes(key)
                        ? 'bg-blue-600 text-white'
                        : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Card>

            {/* Communication Style */}
            <Card className="p-6 space-y-4">
              <h2 className="font-medium flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-violet-600" />
                Communication & Conflict
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-2">
                    Their Communication Style
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: 'direct', label: 'Direct & Clear' },
                      { key: 'balanced', label: 'Balanced' },
                      { key: 'subtle', label: 'Subtle & Indirect' },
                      { key: 'unsure', label: 'Not Sure' },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        onClick={() => setPartnerCommunicationStyle(key as any)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          partnerCommunicationStyle === key
                            ? 'bg-violet-600 text-white'
                            : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-2">
                    How They Handle Conflict
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: 'address-immediately', label: 'Address Immediately' },
                      { key: 'need-space', label: 'Need Space First' },
                      { key: 'avoid', label: 'Tend to Avoid' },
                      { key: 'unsure', label: 'Not Sure' },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        onClick={() => setPartnerConflictStyle(key as any)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          partnerConflictStyle === key
                            ? 'bg-violet-600 text-white'
                            : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Lifestyle */}
            <Card className="p-6 space-y-4">
              <h2 className="font-medium flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-orange-600" />
                Lifestyle Preferences
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-2">
                    Social Energy Level
                  </label>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-neutral-600 w-20">Introverted</span>
                    <Slider
                      value={[partnerSocialPreference]}
                      onValueChange={(v) => setPartnerSocialPreference(v[0])}
                      max={100}
                      step={1}
                      className="flex-1"
                    />
                    <span className="text-xs text-neutral-600 w-20 text-right">Extroverted</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-2">
                    Activity Level
                  </label>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-neutral-600 w-20">Homebody</span>
                    <Slider
                      value={[partnerActivityLevel]}
                      onValueChange={(v) => setPartnerActivityLevel(v[0])}
                      max={100}
                      step={1}
                      className="flex-1"
                    />
                    <span className="text-xs text-neutral-600 w-20 text-right">Adventurous</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Attachment Style */}
            <Card className="p-6 space-y-4">
              <h2 className="font-medium flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-600" />
                Attachment Style (if known)
              </h2>
              
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'secure', label: 'Secure', desc: 'Comfortable with intimacy' },
                  { key: 'anxious', label: 'Anxious', desc: 'Seeks reassurance' },
                  { key: 'avoidant', label: 'Avoidant', desc: 'Values independence' },
                  { key: 'fearful', label: 'Fearful', desc: 'Wants but fears closeness' },
                  { key: 'unsure', label: 'Not Sure', desc: '' },
                ].map(({ key, label, desc }) => (
                  <button
                    key={key}
                    onClick={() => setPartnerAttachment(key as any)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      partnerAttachment === key
                        ? 'bg-purple-600 text-white'
                        : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                    }`}
                  >
                    <div>{label}</div>
                    {desc && <div className="text-xs opacity-70">{desc}</div>}
                  </button>
                ))}
              </div>
            </Card>

            {/* Calculate Button */}
            <Button
              onClick={calculateCompatibility}
              className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
              size="lg"
            >
              <Scale className="w-5 h-5 mr-2" />
              Calculate Alignment
            </Button>
          </div>
        )}

        {step === 'results' && analysis && (
          <div className="space-y-6">
            {/* Overall Score */}
            <Card className={`p-8 ${getScoreBg(compatibilityScore)}`}>
              <div className="text-center space-y-4">
                <div className={`text-6xl font-bold ${getScoreColor(compatibilityScore)}`}>
                  {compatibilityScore}%
                </div>
                <div>
                  <h2 className="text-2xl font-medium text-neutral-900">
                    {getScoreLabel(compatibilityScore)}
                  </h2>
                  <p className="text-neutral-700 mt-2">
                    Alignment score with {partnerName}
                  </p>
                </div>
              </div>
            </Card>

            {/* Breakdown */}
            <Card className="p-6 space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-violet-600" />
                Alignment Breakdown
              </h3>
              
              <div className="space-y-3">
                {[
                  { label: 'Core Values', score: analysis.valueAlignment, weight: '30%' },
                  { label: 'Life Goals', score: analysis.goalAlignment, weight: '25%' },
                  { label: 'Lifestyle', score: analysis.lifestyleAlignment, weight: '20%' },
                  { label: 'Communication', score: analysis.communicationAlignment, weight: '15%' },
                  { label: 'Attachment', score: analysis.attachmentCompatibility, weight: '10%' },
                ].map(({ label, score, weight }) => (
                  <div key={label}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-neutral-700">{label}</span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{weight}</Badge>
                        <span className={`text-sm font-medium ${getScoreColor(score)}`}>
                          {score}%
                        </span>
                      </div>
                    </div>
                    <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          score >= 70 ? 'bg-green-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${score}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Strengths */}
            {analysis.strengths.length > 0 && (
              <Card className="p-6 space-y-4 bg-green-50 border-green-200">
                <h3 className="font-medium flex items-center gap-2 text-green-900">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Relationship Strengths
                </h3>
                <ul className="space-y-2">
                  {analysis.strengths.map((strength: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-green-900">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{strength}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Challenges */}
            {analysis.challenges.length > 0 && (
              <Card className="p-6 space-y-4 bg-yellow-50 border-yellow-200">
                <h3 className="font-medium flex items-center gap-2 text-yellow-900">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  Potential Challenges
                </h3>
                <ul className="space-y-2">
                  {analysis.challenges.map((challenge: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-yellow-900">
                      <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <span>{challenge}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Recommendations */}
            {analysis.recommendations.length > 0 && (
              <Card className="p-6 space-y-4 bg-violet-50 border-violet-200">
                <h3 className="font-medium flex items-center gap-2 text-violet-900">
                  <Sparkles className="w-5 h-5 text-violet-600" />
                  Recommendations
                </h3>
                <ul className="space-y-2">
                  {analysis.recommendations.map((rec: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-violet-900">
                      <span className="w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">
                        {idx + 1}
                      </span>
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => setStep('input')}
                variant="outline"
                className="flex-1"
              >
                Calculate Another Alignment
              </Button>
              <Button
                onClick={() => navigate('/compatibility-profile')}
                className="flex-1 bg-violet-600 hover:bg-violet-700"
              >
                Back to Profile
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
